import { PcbNodeTypes, } from "@buildwithflux/models";
import { isEmpty } from "lodash";
import { getNodesOfType } from "../nodes";
import { generateValidatorsFromFeatureFlag } from "./validatorRegistry";
/**
 * Mutates the document change to include the uids of neighbors of the removed nodes, in the update portion of the document change payload
 */
function mutateDocumentChangeIncludeUpdateNeighbors(neighborsOfChangedNodes, documentChange) {
    if (neighborsOfChangedNodes.length === 0) {
        return documentChange;
    }
    const pcbLayoutNodes = documentChange.payload.pcbLayoutNodes;
    if (!pcbLayoutNodes) {
        documentChange.payload.pcbLayoutNodes = { replace: neighborsOfChangedNodes };
        return;
    }
    if (pcbLayoutNodes.replace) {
        pcbLayoutNodes.replace.push(...neighborsOfChangedNodes);
        return;
    }
    pcbLayoutNodes.replace = [...neighborsOfChangedNodes];
}
/**
 * This class is responsible for managing the DRC recalculation process.
 *
 * The idea of this class is to club/collect all changes associated to a baking operation
 * (e.g. methods in PcbBakedGoodsManager) and then send them to the DRC service for recalculation.
 *
 * The `recalculate` method should be called only once per operation once all changes have been collected.
 */
export class DrcManager {
    pcbPrimitiveStore;
    pcbConnectivityGraph;
    pcbTracingGraph;
    documentService;
    logger;
    featureFlagEnabledValidators;
    validatorOverrides;
    inputContextAccessors;
    callbacks;
    payload = {
        pcbLayoutNodes: {},
        pcbLayoutRuleSets: {},
    };
    // WARNING: This causes a side effect that invalidates some store states, so consider this as a (bad) transaction wrapper
    constructor(pcbPrimitiveStore, pcbConnectivityGraph, pcbTracingGraph, documentService, logger, featureFlagEnabledValidators = {}, validatorOverrides = {}, inputContextAccessors, callbacks) {
        this.pcbPrimitiveStore = pcbPrimitiveStore;
        this.pcbConnectivityGraph = pcbConnectivityGraph;
        this.pcbTracingGraph = pcbTracingGraph;
        this.documentService = documentService;
        this.logger = logger;
        this.featureFlagEnabledValidators = featureFlagEnabledValidators;
        this.validatorOverrides = validatorOverrides;
        this.inputContextAccessors = inputContextAccessors;
        this.callbacks = callbacks;
        pcbConnectivityGraph.invalidateGraph();
        pcbTracingGraph.invalidateGraph();
    }
    /**
     * Add a change (addition, removal, etc.) to the payload for nodes
     * @param operation - The operation to be performed on the nodes (add, remove, replace, set)
     * @param nodes - The nodes to be changed (can be a map of nodes or an array of nodes)
     */
    recordNodesChange(operation, nodes) {
        const nodesPayload = this.payload.pcbLayoutNodes;
        const nodeUids = Array.isArray(nodes) ? nodes.map((node) => node.uid) : Object.keys(nodes);
        const changesForOperation = nodesPayload[operation];
        if (changesForOperation) {
            changesForOperation.push(...nodeUids);
        }
        else {
            nodesPayload[operation] = nodeUids;
        }
    }
    /**
     * Add a change (addition, removal, etc.) to the payload for rule sets
     * @param operation - The operation to be performed on the rule sets (add, remove, replace, set)
     * @param ruleSets - The rule sets to be changed (can be a map of rule sets or an array of rule sets)
     */
    recordRuleSetsChange(operation, ruleSets) {
        const ruleSetsPayload = this.payload.pcbLayoutRuleSets;
        const ruleSetUids = Array.isArray(ruleSets)
            ? ruleSets.map((ruleSet) => (typeof ruleSet === "string" ? ruleSet : ruleSet.uid))
            : Object.keys(ruleSets);
        const changesForOperation = ruleSetsPayload[operation];
        if (changesForOperation) {
            changesForOperation.push(...ruleSetUids);
        }
        else {
            ruleSetsPayload[operation] = ruleSetUids;
        }
    }
    /**
     * Recalculate the DRC results based on the collected changes, and update the store with the results.
     *
     * If this is the first run, the DRC validators are set based on the feature flags,
     * and we subscribe to the feature flags to update the validators when they change.
     *
     * The collected changes are cleaned up before sending them to the DRC service to
     * avoid sending unnecessary data.
     */
    recalculate() {
        // Cleanup the payload before sending it to the DRC service
        // Don't send empty payloads
        const cleanedPayload = this.cleanupPayload();
        if (!cleanedPayload) {
            return;
        }
        const documentChange = {
            kind: "subCollection",
            payload: cleanedPayload,
        };
        // TODO: await/async only needed for clipperjs - if DRC checks that depend on clipper
        // are eventually migrated to worker(s) then this await/async can be removed
        const inputs = this.prepareInputs(documentChange);
        if (!inputs) {
            return;
        }
        // When rules on a RouteSegment/Trace are changed, we in some cases need to notify and re-bake trace
        // nodes that are connected to the changing trace node(s). Node re-baking in Flux is typically triggered
        // by either direct update to it's rules (object-level), or by rule updates inherited from an ancestor
        // node. Curved Traces however introduce a new dependency case - changes to an object-level trace shape
        // rule, usually require any connected trace(s) to also be re-baked. That's to allow required geometry
        // updates on those connected traces to occur (eg to ensure curves on connected trace match that of
        // changing trace). The updateGraph returns a collection of changed neighbor nodes, which is currently
        // used to trigger re-baking of connected traces.
        const { neighborsOfChangedNodes } = this.pcbTracingGraph.updateGraph(inputs);
        // This adds to the document change payload, the uids of nodes that were graph-neighbors of
        // nodes removed from the tracing graph. The primary purpose of this is to ensure route segments
        // connected to a removed, or changed (eg via rule update), route segment are re-rendered/updated in the
        // canvas
        mutateDocumentChangeIncludeUpdateNeighbors(neighborsOfChangedNodes, documentChange);
        // Updates the shape store
        this.pcbPrimitiveStore.applyChange(documentChange, inputs.pcbLayoutNodes, this.pcbTracingGraph);
        // PCB Connectivity
        // NOTE: this is actually asynchronous, but we don't await it here
        // because we want to continue with the DRC calculation regardless of the connectivity graph update
        inputs.pcbConnectivityGraphPromise = this.pcbConnectivityGraph.updateGraph(inputs, this.logger);
        // Finally, compute and update DRC
        const validators = generateValidatorsFromFeatureFlag(this.featureFlagEnabledValidators, this.validatorOverrides);
        return () => Promise.all(validators.map(async (validator) => {
            this.callbacks.onValidationStart(validator.problemTypeKey);
            try {
                const validatorOutput = await validator.checkForProblems(inputs);
                const foundProblems = validatorOutput.error ? [] : validatorOutput.foundProblems;
                const error = validatorOutput.error ? validatorOutput.error : false;
                this.callbacks.onValidationFinish(validator.problemTypeKey, foundProblems, error);
            }
            catch (e) {
                this.callbacks.onValidationFinish(validator.problemTypeKey, [], `Internal error: ${e?.message ?? "Unknown error"}`);
            }
        }));
    }
    /** Cleanup the payload before sending it to the DRC service */
    cleanupPayload() {
        const finalPayload = {};
        const { pcbLayoutNodes, pcbLayoutRuleSets } = this.payload;
        if (!isEmpty(pcbLayoutNodes)) {
            const cleanedNodes = this.cleanupPayloadInternal(pcbLayoutNodes);
            if (cleanedNodes) {
                finalPayload.pcbLayoutNodes = cleanedNodes;
            }
        }
        if (!isEmpty(pcbLayoutRuleSets)) {
            const cleanedRuleSets = this.cleanupPayloadInternal(pcbLayoutRuleSets);
            if (cleanedRuleSets) {
                finalPayload.pcbLayoutRuleSets = cleanedRuleSets;
            }
        }
        if (isEmpty(finalPayload)) {
            return undefined;
        }
        return finalPayload;
    }
    /**
     * Cleanup the payload for a specific sub-collection:
     * * Remove duplicates
     * * Remove items that are both added and removed, and put them in the `replace` list
     * * Remove empty operations
     */
    cleanupPayloadInternal(originalPayload) {
        const finalPayload = {};
        for (const operation of Object.keys(originalPayload)) {
            const uids = originalPayload[operation];
            if (uids?.length) {
                const unique = Array.from(new Set(uids));
                if (unique.length) {
                    finalPayload[operation] = unique;
                }
            }
        }
        // If a uid appears in `add` and `remove`, it means it was updated
        // We should remove it from both lists and put it in the `set` list
        const added = finalPayload?.add;
        const removed = finalPayload?.remove;
        if (added?.length && removed?.length) {
            const updated = added.filter((node) => removed.includes(node));
            if (updated.length) {
                (finalPayload.replace ??= []).push(...updated);
                finalPayload.add = added.filter((node) => !updated.includes(node));
                finalPayload.remove = removed.filter((node) => !updated.includes(node));
                if (isEmpty(finalPayload.add))
                    delete finalPayload.add;
                if (isEmpty(finalPayload.remove))
                    delete finalPayload.remove;
            }
        }
        // HACK: Ensure that the nodes that we are adding and replacing do exist in the tree
        // Ideally, we should never need to do this since the changes accumulated in by DrcRecalculator
        // should be in sync with the document tree
        const allNodes = this.documentService.snapshot().pcbLayoutNodes;
        const operationsToCheck = ["add", "replace", "set"];
        for (const operation of operationsToCheck) {
            const uids = finalPayload[operation];
            if (uids?.length) {
                finalPayload[operation] = uids.filter((nodeUid) => allNodes[nodeUid]);
            }
        }
        if (isEmpty(finalPayload)) {
            return undefined;
        }
        return finalPayload;
    }
    /**
     * Prepare the inputs for the DRC calculation
     */ prepareInputs(change) {
        const document = this.inputContextAccessors.getDocumentData();
        if (!document) {
            return null;
        }
        // Prevent expensive DRC calculations if nothing has changed, unless forced
        const bakedNodesChanges = change?.kind === "subCollection" ? change?.payload.pcbLayoutNodes : undefined;
        const globalRulesChange = change?.kind === "subCollection" ? change?.payload.pcbLayoutRuleSets : undefined;
        if (isEmpty(globalRulesChange) && isEmpty(bakedNodesChanges)) {
            return null;
        }
        const elements = document.elements;
        const pcbLayoutRuleSets = document.pcbLayoutRuleSets;
        const pcbLayoutNodes = this.documentService.snapshot().pcbLayoutNodes;
        const allNetNodes = getNodesOfType(pcbLayoutNodes, PcbNodeTypes.net);
        const netMap = Object.fromEntries(allNetNodes.map((node) => [node.uid, node.terminals || {}]));
        const netNameByNetId = Object.fromEntries(allNetNodes.map((node) => [node.uid, node.name ?? node.uid]));
        const shapesMap = this.pcbPrimitiveStore.getFillShapesMap();
        const nodeUidList = this.pcbPrimitiveStore.getAllNodeUidsWithShapes();
        const nodeShapesMap = this.pcbPrimitiveStore.getAllNodePrimitivesMap();
        const containerMap = this.pcbPrimitiveStore.getAllContainersMap();
        return {
            documentUid: document.uid,
            documentName: document.name,
            documentDescription: document.description,
            pcbLayoutNodes,
            elements,
            pcbLayoutRuleSets,
            netMap,
            netNameByNetId,
            nodeUidList,
            nodeShapesMap,
            containerMap,
            shapesMap,
            bakedNodesChanges,
            pcbTracingGraph: this.pcbTracingGraph,
            pcbPrimitiveStore: this.pcbPrimitiveStore,
        };
    }
}
//# sourceMappingURL=DrcManager.js.map